import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useThemeContext } from "../../../context/themeContext";
import { useHeaderContext } from "../../../context/headerContext";
import { useUserContext } from "../../../context/userContext";
import { useDrawerContext } from "../../../context/drawerContext";
import { APIProvider, Map, useMapsLibrary } from "@vis.gl/react-google-maps";
import ChevronIconRight from "/public/images/icons/chevronRight.svg?react";
import CloseIcon from "/public/images/icons/close.svg?react";
import { Link } from "react-router-dom";
import fetchWithRefresh from "../../../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import DropDown from "../../../components/Dropdown";
import TextInput from "../../../components/TextInput";
import Button from "../../../components/Button";
import SelectInput from "../../../components/SelectInput";
import AdvancedPhoneInput from "../../../components/AdvancedPhoneInput";
import TextArea from "../../../components/TextArea";
import { days, months, years } from "../../../utils/calendar";

function IncompleteDetailsForm({ participant, onSubmit, onBack, onClose }) {
  const formId = `incomplete-form-${participant.bookingParticipantId ?? participant.participantId}`;

  const parseDOB = (dob) => {
    if (!dob) return { day: "", month: "", year: "" };
    const isoMatch = dob.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return { day: isoMatch[3], month: isoMatch[2], year: isoMatch[1] };
    const dmyMatch = dob.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (dmyMatch) return { day: dmyMatch[1], month: dmyMatch[2], year: dmyMatch[3] };
    return { day: "", month: "", year: "" };
  };

  const parsed = parseDOB(participant.participantDOB);
  const [dobDay, setDobDay] = useState(parsed.day);
  const [dobMonth, setDobMonth] = useState(parsed.month);
  const [dobYear, setDobYear] = useState(parsed.year);
  const [phoneErrors, setPhoneErrors] = useState([]);

  const dobValue = dobDay && dobMonth && dobYear ? `${dobYear}-${dobMonth}-${dobDay}` : "";

  return (
    <div className="w-full flex flex-col" style={{ maxHeight: "75vh" }}>
      <div className="flex items-center justify-between w-full mb-6 flex-shrink-0">
        <button
          className="text-[12px] font-semibold tracking-widest uppercase underline"
          onClick={onBack}
        >
          Back
        </button>
        <h2 className="leading-5 tracking-widest uppercase text-[12px]">
          {participant.participantName ?? "Participant"}
        </h2>
        <CloseIcon className="cursor-pointer" onClick={onClose} />
      </div>
      <form
        key={formId}
        id={formId}
        onSubmit={onSubmit}
        className="flex flex-col gap-4 overflow-y-auto flex-1 pb-2 px-[2px]"
      >
        <TextInput label="Name" name="participantName" defaultValue={participant.participantName ?? ""} />
        <TextInput label="Email" name="email" type="email" defaultValue={participant.guestEmail ?? ""} />
        <div>
          <span className="text-sportsBlack font-medium text-base leading-[26px] mb-3 block">Date of Birth</span>
          <div className="grid grid-cols-3 gap-[6px]">
            <SelectInput
              label="Day" placeholder="Select" name="dobDay"
              defaultValue={dobDay}
              options={days.map((day, i) => <option key={day.value + "_" + i} value={day.value}>{day.label}</option>)}
              onChange={(e) => setDobDay(e.target.value)}
            />
            <SelectInput
              label="Month" placeholder="Select" name="dobMonth"
              defaultValue={dobMonth}
              options={months.map((month, i) => <option key={month.value + "_" + i} value={month.value}>{month.label}</option>)}
              onChange={(e) => setDobMonth(e.target.value)}
            />
            <SelectInput
              label="Year" placeholder="Select" name="dobYear"
              defaultValue={dobYear}
              options={years.map((year, i) => <option key={year.value + "_" + i} value={year.value}>{year.label}</option>)}
              onChange={(e) => setDobYear(e.target.value)}
            />
          </div>
          <input type="hidden" name="dateOfBirth" value={dobValue} readOnly />
        </div>
        <TextInput label="Primary Emergency Contact Name (ICE)" name="primaryICEName" defaultValue={participant.primaryEmergencyContactName ?? ""} />
        <AdvancedPhoneInput
          label="Primary Emergency Contact Number"
          inputName="primaryICENumber"
          defaultValue={participant.primaryContactNumber ?? ""}
          error={phoneErrors}
          setError={setPhoneErrors}
        />
        <TextInput label="Primary Emergency Contact Relationship" name="primaryICERelationship" defaultValue={participant.primaryContactRelationship ?? ""} />
        <TextInput label="Primary Medical Condition" name="primaryMedicalCondition" defaultValue={participant.primaryMedicalCondition ?? ""} />
        <TextArea label="Notes" name="notes" defaultValue={participant.notes ?? ""} />
      </form>
      <div className="pt-3 flex-shrink-0">
        <button
          type="submit"
          form={formId}
          className="w-full text-base leading-6 flex flex-row gap-[10px] justify-center items-center p-3 rounded-1 hover:shadow-md active:shadow-md font-semibold bg-sportsBlue text-white"
        >
          Save
        </button>
      </div>
    </div>
  );
}

IncompleteDetailsForm.propTypes = {
  participant: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

const formatDOB = (dob) => {
  if (!dob) return dob;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dob)) return dob;
  const isoMatch = dob.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
  return dob;
};

function GeocodedMap({ address }) {
  const geocodingLib = useMapsLibrary("geocoding");
  const [center, setCenter] = useState(null);

  useEffect(() => {
    if (!geocodingLib || !address) return;
    const geocoder = new geocodingLib.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
      if (status === "OK" && results[0]) {
        const loc = results[0].geometry.location;
        setCenter({ lat: loc.lat(), lng: loc.lng() });
      }
    });
  }, [geocodingLib, address]);

  if (!center) return null;

  return (
    <Map
      style={{ width: "100%", height: "174px", borderRadius: "8px", border: "1px solid #E3E3E3" }}
      defaultCenter={center}
      defaultZoom={15}
      gestureHandling="greedy"
      disableDefaultUI={true}
    />
  );
}

export default function ParticipantBooking() {
  const { setTitle, setBackButton, setFilterFunc, setBackButtonFunc, setEditFunc } = useHeaderContext();
  const [pillFilter, setPillFilter] = useState("All");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  const { state } = useLocation();
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { theme } = useThemeContext();
  const { user, logout, updateTokens } = useUserContext();
  const { setOpen, setDrawerContent } = useDrawerContext();

  useEffect(() => {
    setBackButton(true);
    setFilterFunc();
    if (theme !== "staff") setTitle("Your Booking");

    // Seed from router state for instant render while fetch completes
    if (state?.booking) setBooking(state.booking);

    if (theme === "staff") {
      const fetchBooking = async () => {
        try {
          const response = await fetchWithRefresh(
            `${import.meta.env.VITE_API_URL}scan/bookingcodescan?ScanID=${bookingId}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user?.token}`,
              },
            },
            logout, updateTokens, (message) => { toast.info(message); }
          );
          if (response.ok) {
            const data = await response.json();
            setBooking(data);
          } else {
            toast.error("Failed to load booking.");
          }
        } catch {
          toast.error("Failed to load booking.");
        } finally {
          setLoading(false);
        }
      };
      fetchBooking();
    } else {
      // Fetch fresh booking data for participant theme to avoid stale state
      const fetchFreshBooking = async () => {
        try {
          const url = `${import.meta.env.VITE_API_URL}bookings/bookings?userLoginId=${user?.id}&page=0&pagesize=100&hidenotbooked=1`;
          const response = await fetchWithRefresh(url, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user?.token}`,
            },
          }, logout, updateTokens, (message) => { toast.info(message); });
          if (response.ok) {
            const data = await response.json();
            const list = Array.isArray(data) ? data : (data.bookings ?? data.data ?? data.$values ?? []);
            const fresh = list.find((b) => b.bookingId === bookingId);
            if (fresh) setBooking(fresh);
          }
        } catch {
          // silently fall back to seeded state
        } finally {
          setLoading(false);
        }
      };
      fetchFreshBooking();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (theme !== "staff") return;
    const name = (booking?.bookableThingName ?? booking?.orgPartnerFriendlyName ?? "").replace(/\b\w/g, c => c.toUpperCase());
    setTitle(name ? `${name} (${filteredParticipants?.length ?? 0})` : "Booking");
    setBackButtonFunc(() => navigate("/bookings"));
  }, [pillFilter, theme]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePillClick = (pill) => {
    setPillFilter(pill);
  };

  const handleSaveGuest = async (e, participant) => {
    e.preventDefault();
    const form = e.target;
    const payload = {
      bookingParticipantId: participant.bookingParticipantId,
      participantId: participant.participantId,
      participantName: form.elements.participantName.value,
      email: form.elements.email.value,
      dateOfBirth: form.elements.dateOfBirth.value,
      primaryICEName: form.elements.primaryICEName.value,
      primaryICENumber: form.elements.primaryICENumber.value,
      primaryICERelationship: form.elements.primaryICERelationship.value,
      primaryMedicalCondition: form.elements.primaryMedicalCondition.value,
      notes: form.elements.notes.value,
    };
    try {
      const response = await fetchWithRefresh(
        `${import.meta.env.VITE_API_URL}Bookings/guestfillrequest`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${user?.token}` },
          body: JSON.stringify(payload),
        },
        logout, updateTokens, (message) => toast.info(message)
      );
      if (response.ok) {
        toast.success("Guest details updated.");
        setOpen(false);
        // Update local booking state so the DropDown reflects the new values immediately
        setBooking((prev) => ({
          ...prev,
          participants: (prev.participants ?? []).map((p) =>
            p.bookingParticipantId === participant.bookingParticipantId
              ? {
                  ...p,
                  participantName: payload.participantName,
                  guestEmail: payload.email,
                  participantDOB: payload.dateOfBirth,
                  primaryEmergencyContactName: payload.primaryICEName,
                  primaryContactNumber: payload.primaryICENumber,
                  primaryContactRelationship: payload.primaryICERelationship,
                  primaryMedicalCondition: payload.primaryMedicalCondition,
                  notes: payload.notes,                  isIncomplete: 0,
                  IsIncomplete: 0,                }
              : p
          ),
        }));
      } else {
        toast.error("Failed to update guest details.");
      }
    } catch {
      toast.error("Failed to update guest details.");
    }
  };

  const fetchGuestFillData = async (participantId) => {
    try {
      const response = await fetchWithRefresh(
        `${import.meta.env.VITE_API_URL}Bookings/guestfillrequest?participantId=${participantId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
        },
        logout, updateTokens, (message) => toast.info(message)
      );
      if (response.ok) return await response.json();
    } catch {
      // fall through — form will render with whatever the booking scan returned
    }
    return null;
  };

  const enrichParticipant = (participant, guestData) => ({
    ...participant,
    participantName: guestData.ParticipantName ?? participant.participantName,
    guestEmail: guestData.GuestEmail ?? participant.guestEmail,
    participantDOB: guestData.DateOfBirth ?? participant.participantDOB,
    primaryEmergencyContactName: guestData.PrimaryIceName ?? participant.primaryEmergencyContactName,
    primaryContactNumber: guestData.PrimaryIceNumber ?? participant.primaryContactNumber,
    primaryContactRelationship: guestData.PrimaryIceRelationship ?? participant.primaryContactRelationship,
    primaryMedicalCondition: guestData.PrimaryMedicalCondition ?? participant.primaryMedicalCondition,
    notes: guestData.Notes ?? participant.notes,
  });

  const openGuestEditForm = (participant, editable) => {
    const formId = `guest-edit-form-${participant.bookingParticipantId ?? participant.participantId}`;
    let dobValue = "";
    if (participant.participantDOB) {
      const isoMatch = participant.participantDOB.match(/^\d{4}-\d{2}-\d{2}$/);
      if (isoMatch) {
        const [year, month, day] = participant.participantDOB.split("-");
        dobValue = `${day}/${month}/${year}`;
      } else {
        dobValue = participant.participantDOB;
      }
    }
    setDrawerContent(
      <div className="w-full flex flex-col" style={{ maxHeight: "75vh" }}>
        <div className="flex items-center justify-between w-full mb-6 flex-shrink-0">
          {editable.length > 1 ? (
            <button
              className="text-[12px] font-semibold tracking-widest uppercase underline"
              onClick={() => openEditDrawer()}
            >
              Back
            </button>
          ) : (
            <br />
          )}
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            {participant.participantName ?? "Guest"}
          </h2>
          <CloseIcon className="cursor-pointer" onClick={() => setOpen(false)} />
        </div>
        <form
          key={formId}
          id={formId}
          onSubmit={(e) => handleSaveGuest(e, participant)}
          className="flex flex-col gap-4 overflow-y-auto flex-1 pb-2"
        >
          <TextInput label="Name" name="participantName" defaultValue={participant.participantName ?? ""} />
          <TextInput label="Email" name="email" type="email" defaultValue={participant.guestEmail ?? ""} />
          <TextInput label="Date of Birth" name="dateOfBirth" defaultValue={dobValue} placeholder="DD/MM/YYYY" />
          <TextInput label="Emergency Contact Name" name="primaryICEName" defaultValue={participant.primaryEmergencyContactName ?? ""} />
          <TextInput label="Emergency Contact Number" name="primaryICENumber" defaultValue={participant.primaryContactNumber ?? ""} />
          <TextInput label="Relationship" name="primaryICERelationship" defaultValue={participant.primaryContactRelationship ?? ""} />
          <TextInput label="Medical Condition" name="primaryMedicalCondition" defaultValue={participant.primaryMedicalCondition ?? ""} />
          <TextInput label="Notes" name="notes" defaultValue={participant.notes ?? ""} />
        </form>
        <div className="pt-3 flex-shrink-0">
          <button
            type="submit"
            form={formId}
            className={`w-full text-base leading-6 flex flex-row gap-[10px] justify-center items-center p-3 rounded-1 hover:shadow-md active:shadow-md font-semibold bg-sportsYellow text-sportsBlack`}
          >
            Save
          </button>
        </div>
      </div>
    );
  };

  const openEditDrawer = () => {
    const editable = (booking?.participants ?? []).filter((p) => p.isGuestParticipant === 1 && p.sendGuestFillEmail === 0);
    if (editable.length === 1) {
      setOpen(true);
      openGuestEditForm(editable[0], editable);
      return;
    }
    setOpen(true);
    setDrawerContent(
      <div className="w-full flex flex-col" style={{ maxHeight: "75vh" }}>
        <div className="flex items-center justify-between w-full mb-6 flex-shrink-0">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">Edit Guests</h2>
          <CloseIcon className="cursor-pointer" onClick={() => setOpen(false)} />
        </div>
        <div className="flex flex-col gap-3 overflow-y-auto flex-1">
          {editable.map((participant, i) => {
            const fullIndex = (booking?.participants ?? []).findIndex(p => p.bookingParticipantId === participant.bookingParticipantId || p.participantId === participant.participantId);
            return (
              <button
                key={i}
                className="flex items-center justify-between w-full border border-[#E3E3E3] rounded-lg p-4 bg-white text-left"
                onClick={() => openGuestEditForm(participant, editable)}
              >
                <div className="flex flex-col">
                  <span className="text-[12px] text-sportsDarkGrey">
                    {participant.isLeadParticipant ? "Lead Participant" : `Participant ${fullIndex + 1}`}
                  </span>
                  <span className="font-semibold">
                    {participant.participantName?.replace(/\b\w/g, c => c.toUpperCase()) ?? "Guest"}
                  </span>
                </div>
                <ChevronIconRight className="w-5 h-5 fill-black flex-shrink-0" />
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (theme !== "participant" || !booking) return;
    const editable = (booking.participants ?? []).filter((p) => p.isGuestParticipant === 1 && p.sendGuestFillEmail === 0);
    const resendable = (booking.participants ?? []).filter((p) => p.isGuestParticipant === 1 && p.sendGuestFillEmail === 1);
    if (editable.length > 0 || resendable.length > 0) {
      setEditFunc(() => {
        if (editable.length > 0 && resendable.length === 0) {
          openEditDrawer();
        } else if (resendable.length > 0 && editable.length === 0) {
          openResendEmailDrawer();
        } else {
          openActionDrawer(editable, resendable);
        }
      });
    }
  }, [booking]); // eslint-disable-line react-hooks/exhaustive-deps

  const openActionDrawer = (editable, resendable) => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full flex flex-col" style={{ maxHeight: "75vh" }}>
        <div className="flex items-center justify-between w-full mb-6 flex-shrink-0">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">Manage Guests</h2>
          <CloseIcon className="cursor-pointer" onClick={() => setOpen(false)} />
        </div>
        <div className="flex flex-col gap-3 overflow-y-auto flex-1">
          {editable.length > 0 && (
            <>
              <p className="text-[12px] font-semibold uppercase tracking-widest text-sportsDarkGrey">Complete Details</p>
              {editable.map((participant, i) => {
                const fullIndex = (booking?.participants ?? []).findIndex(p => p.bookingParticipantId === participant.bookingParticipantId || p.participantId === participant.participantId);
                return (
                  <button
                    key={`edit-${i}`}
                    className="flex items-center justify-between w-full border border-[#E3E3E3] rounded-lg p-4 bg-white text-left"
                    onClick={() => openGuestEditForm(participant, editable)}
                  >
                    <div className="flex flex-col">
                      <span className="text-[12px] text-sportsDarkGrey">
                        {participant.isLeadParticipant ? "Lead Participant" : `Participant ${fullIndex + 1}`}
                      </span>
                      <span className="font-semibold">
                        {participant.participantName?.replace(/\b\w/g, c => c.toUpperCase()) ?? "Guest"}
                      </span>
                    </div>
                    <ChevronIconRight className="w-5 h-5 fill-black flex-shrink-0" />
                  </button>
                );
              })}
            </>
          )}
          {resendable.length > 0 && (
            <>
              <p className="text-[12px] font-semibold uppercase tracking-widest text-sportsDarkGrey mt-4">Re-send Email</p>
              {resendable.map((participant, i) => {
                const fullIndex = (booking?.participants ?? []).findIndex(p => p.bookingParticipantId === participant.bookingParticipantId || p.participantId === participant.participantId);
                return (
                  <button
                    key={`resend-${i}`}
                    className="flex items-center justify-between w-full border border-[#E3E3E3] rounded-lg p-4 bg-white text-left"
                    onClick={() => openResendEmailForm(participant)}
                  >
                    <div className="flex flex-col">
                      <span className="text-[12px] text-sportsDarkGrey">
                        {participant.isLeadParticipant ? "Lead Participant" : `Participant ${fullIndex + 1}`}
                      </span>
                      <span className="font-semibold">
                        {participant.participantName?.replace(/\b\w/g, c => c.toUpperCase()) ?? "Guest"}
                      </span>
                    </div>
                    <ChevronIconRight className="w-5 h-5 fill-black flex-shrink-0" />
                  </button>
                );
              })}
            </>
          )}
        </div>
      </div>
    );
  };

  const openResendEmailDrawer = () => {
    const resendable = (booking?.participants ?? []).filter((p) => p.isGuestParticipant === 1 && p.sendGuestFillEmail === 1);
    if (resendable.length === 1) {
      setOpen(true);
      openResendEmailForm(resendable[0]);
      return;
    }
    setOpen(true);
    setDrawerContent(
      <div className="w-full flex flex-col" style={{ maxHeight: "75vh" }}>
        <div className="flex items-center justify-between w-full mb-6 flex-shrink-0">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">Re-send Email</h2>
          <CloseIcon className="cursor-pointer" onClick={() => setOpen(false)} />
        </div>
        <div className="flex flex-col gap-3 overflow-y-auto flex-1">
          {resendable.map((participant, i) => {
            const fullIndex = (booking?.participants ?? []).findIndex(p => p.bookingParticipantId === participant.bookingParticipantId || p.participantId === participant.participantId);
            return (
              <button
                key={i}
                className="flex items-center justify-between w-full border border-[#E3E3E3] rounded-lg p-4 bg-white text-left"
                onClick={() => openResendEmailForm(participant)}
              >
                <div className="flex flex-col">
                  <span className="text-[12px] text-sportsDarkGrey">
                    {participant.isLeadParticipant ? "Lead Participant" : `Participant ${fullIndex + 1}`}
                  </span>
                  <span className="font-semibold">
                    {participant.participantName?.replace(/\b\w/g, c => c.toUpperCase()) ?? "Guest"}
                  </span>
                </div>
                <ChevronIconRight className="w-5 h-5 fill-black flex-shrink-0" />
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const openResendEmailForm = (participant) => {
    const resendable = (booking?.participants ?? []).filter((p) => p.isGuestParticipant === 1 && p.sendGuestFillEmail === 1);
    const showBack = resendable.length > 1;
    
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          {showBack ? (
            <button
              className="text-[12px] font-semibold tracking-widest uppercase underline"
              onClick={() => openResendEmailDrawer()}
            >
              Back
            </button>
          ) : (
            <br />
          )}
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            RE-SEND EMAIL
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <div className="mt-6 flex flex-col gap-4 justify-stretch">
          <h3 className="text-[24px]">Re-send guest fill email?</h3>
          <p className=" text-sportsDarkGrey">
            Would you like to update the email address or name before re-sending?
          </p>
          <Button variant='primary' onClick={() => openResendEmailEditForm(participant)}>Yes, change email/name</Button>
          <Button variant='tertiary' className="underline" onClick={() => handleResendGuestEmail(participant, true)}>No, re-send as is</Button>
        </div>
      </div>
    );
  };

  const openResendEmailEditForm = (participant) => {
    const formId = `resend-email-edit-form-${participant?.bookingParticipantId}`;
    setDrawerContent(
      <div className="w-full flex flex-col" style={{ maxHeight: "75vh" }}>
        <div className="flex items-center justify-between w-full mb-6 flex-shrink-0">
          <button
            className="text-[12px] font-semibold tracking-widest uppercase underline"
            onClick={() => openResendEmailForm(participant)}
          >
            Back
          </button>
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            Re-send Email
          </h2>
          <CloseIcon className="cursor-pointer" onClick={() => setOpen(false)} />
        </div>
        <form
          key={formId}
          id={formId}
          onSubmit={(e) => handleResendGuestEmail(participant, false, e)}
          className="flex flex-col gap-4 overflow-y-auto flex-1"
        >
          <TextInput 
            label="Name" 
            name="resendParticipantName" 
            defaultValue={participant?.guestEmailName || ''} 
          />
          <TextInput 
            label="Email" 
            name="resendEmail" 
            type="email" 
            defaultValue={participant?.guestEmail || ''}
          />
          <div className="pt-3 flex-shrink-0">
            <button
              type="submit"
              className={`w-full text-base leading-6 flex flex-row gap-[10px] justify-center items-center p-3 rounded-1 hover:shadow-md active:shadow-md font-semibold bg-sportsYellow text-sportsBlack`}
            >
              Send
            </button>
          </div>
        </form>
      </div>
    );
  };

  const handleResendGuestEmail = async (participant, sendAsIs = false, e = null) => {
    if (e) e.preventDefault();
    
    let name, email;
    
    if (sendAsIs) {
      name = participant?.guestEmailName || '';
      email = participant?.guestEmail || '';
    } else {
      const form = e?.target;
      name = form?.elements.resendParticipantName.value || '';
      email = form?.elements.resendEmail.value || '';
    }
    
    if (!name || !email) {
      toast.error('Please enter both name and email');
      return;
    }

    try {
      const url = `${import.meta.env.VITE_API_URL}Bookings/regenerateguestemailtoken/${participant.bookingParticipantId}?name=${encodeURIComponent(name)}&email=${encodeURIComponent(email)}`;
      
      const response = await fetchWithRefresh(
        url,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
        },
        logout,
        updateTokens,
        (message) => toast.info(message)
      );

      if (response.ok) {
        toast.success('Guest fill email re-sent successfully');
        setOpen(false);
        // Update local booking state to reflect the new email values
        setBooking((prev) => ({
          ...prev,
          participants: (prev.participants ?? []).map((p) =>
            p.bookingParticipantId === participant.bookingParticipantId
              ? {
                  ...p,
                  participantName: name,
                  guestEmailName: name,
                  guestEmail: email,
                }
              : p
          ),
        }));
      } else {
        toast.error('Failed to re-send guest email');
      }
    } catch (error) {
      toast.error('Failed to re-send guest email');
    }
  };

  const openCompleteDetailsForm = async (participant) => {
    setDrawerContent(
      <div className="w-full flex justify-center items-center py-16">
        <div className="w-8 h-8 border-4 border-sportsBlue border-t-transparent rounded-full animate-spin" />
      </div>
    );

    const guestData = await fetchGuestFillData(participant.participantId);
    const enriched = guestData ? enrichParticipant(participant, guestData) : participant;

    setDrawerContent(
      <IncompleteDetailsForm
        participant={enriched}
        onSubmit={(e) => handleSaveGuest(e, participant)}
        onBack={() => openIncompleteWarningDrawer(participant)}
        onClose={() => setOpen(false)}
      />
    );
  };

  const openIncompleteWarningDrawer = (participant) => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full flex flex-col gap-6">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">Incomplete Details</h2>
          <CloseIcon className="cursor-pointer" onClick={() => setOpen(false)} />
        </div>
        <div className="flex flex-col gap-2">
          <h3 className="text-[24px]">This participant can not be checked in until they complete their details</h3>
        </div>
        <div className="flex flex-col gap-3">
          <Button onClick={() => openCompleteDetailsForm(participant)}>Complete Details</Button>
          <Button variant="quaternary" onClick={() => setOpen(false)}>Close</Button>
        </div>
      </div>
    );
  };

  const isBookingCompleted = (booking?.status ?? booking?.Status) === 4;

  const filteredParticipants = (booking?.participants ?? []).filter((participant) => {
    if (pillFilter === "All") {
      return true;
    } else if (pillFilter === "Active") {
      return participant.isBookedIn === 1 || participant.bandStatus === "active";
    } else if (pillFilter === "Not Assigned") {
      return participant.isBookedIn !== 1 && participant.bandStatus !== "active";
    }
    return false;
  });

  if (!booking) {
    return loading ? (
      <div className="flex justify-center items-center py-16">
        <div className="w-8 h-8 border-4 border-sportsBlue border-t-transparent rounded-full animate-spin" />
      </div>
    ) : (
      <p className="p-6">No booking information available.</p>
    );
  }

  if (theme === "participant") {
    const formattedDate = booking.dateSlot
      ? new Date(booking.dateSlot).toLocaleDateString("en-GB", { weekday: "long", year: "numeric", month: "long", day: "numeric" })
      : "";
    const venueName = booking.orgPartnerFriendlyName?.replace(/\b\w/g, c => c.toUpperCase());

    const getDuration = (start, end) => {
      if (!start || !end) return null;
      const [sh, sm] = start.split(":").map(Number);
      const [eh, em] = end.split(":").map(Number);
      const totalMins = (eh * 60 + em) - (sh * 60 + sm);
      if (totalMins <= 0) return null;
      const h = Math.floor(totalMins / 60);
      const m = totalMins % 60;
      return h > 0 && m > 0 ? `${h}h ${m}m` : h > 0 ? `${h}h` : `${m}m`;
    };
    const duration = getDuration(booking.startTime, booking.endTime);
    const bookableThingName = booking.bookableThingName ?? null;
    const address = booking.venueAddress ?? null;
    const postcode = booking.venuePostcode ?? null;
    const fullAddress = [address, postcode].filter(Boolean).join(", ");
    const venueImage = booking.directoryDisplayImage ?? null;
    const formattedTime = booking.startTime ? booking.startTime.slice(0, 5) : "";

    return (
      <div className="flex flex-col gap-y-[30px] p-[24px] bg-sportsLightGrey min-h-screen mb-[60px]">
        {booking.status === -1 && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
            <p className="text-red-600 font-semibold text-[13px] uppercase tracking-widest">Cancelled</p>
            <p className="text-red-500 text-[12px] mt-1">This booking has been cancelled.</p>
          </div>
        )}
        <div>
          <p className="text-[12px] font-semibold tracking-widest uppercase text-sportsDarkGrey mb-1">Booking At</p>
          <h2 className="text-[24px] font-semibold text-sportsBlack">{venueName}</h2>
        </div>

        <div className="w-full h-[197px] rounded-[8px] border border-[#E3E3E3] bg-[#E3E3E3] overflow-hidden">
          {venueImage && (
            <img src={venueImage} alt={venueName} className="w-full h-full object-cover" />
          )}
        </div>

        <div className="flex flex-col gap-y-4">
          {bookableThingName && (
            <div className="flex flex-col gap-y-1">
              <p className="font-semibold text-[14px]">Activity</p>
              <p className="text-sportsDarkGrey">{bookableThingName}</p>
            </div>
          )}
          <div className="flex flex-col gap-y-1">
            <p className="font-semibold text-[14px]">Date</p>
            <p className="text-sportsDarkGrey">{formattedDate}</p>
          </div>
          <div className="flex flex-col gap-y-1">
            <p className="font-semibold text-[14px]">Time</p>
            <p className="text-sportsDarkGrey">{formattedTime}</p>
          </div>
          {duration && (
            <div className="flex flex-col gap-y-1">
              <p className="font-semibold text-[14px]">Duration</p>
              <p className="text-sportsDarkGrey">{duration}</p>
            </div>
          )}
        </div>

        {booking.participants?.length > 0 && (
          <div>
            <p className="font-semibold text-[14px] mb-3">Participants</p>
            <div className="flex flex-col gap-y-3">
              {booking.participants.map((participant, index) => (
                <DropDown
                  key={index}
                  locked={participant.sendGuestFillEmail === 2}
                  title={
                    <div className="flex flex-row items-center gap-x-3">
                      {participant.sendGuestFillEmail > 0 && (
                        <div className="w-[50px] h-[50px] flex-shrink-0 border border-[#E3E3E3] rounded-[4px] flex items-center justify-center">
                          <svg className="w-5 h-5 text-sportsDarkGrey" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                          </svg>
                        </div>
                      )}
                      <div className="flex flex-col">
                        <span className="text-[12px] font-normal text-sportsDarkGrey">{participant.isLeadParticipant ? "Lead Participant" : `Participant ${index + 1}`}</span>
                        <span className="font-semibold text-[16px]">{participant.participantName?.replace(/\b\w/g, c => c.toUpperCase())}</span>
                      </div>
                    </div>
                  }
                >
                  <div className="flex flex-col gap-y-4 pt-2">
                    {participant.sendGuestFillEmail === 1 ? (
                      participant.guestEmail && (
                        <div className="flex flex-col gap-y-1">
                          <p className="font-semibold text-[13px]">Email</p>
                          <p className="text-sportsDarkGrey text-[13px]">{participant.guestEmail}</p>
                        </div>
                      )
                    ) : (
                      <>
                        {participant.participantDOB && (
                          <div className="flex flex-col gap-y-1">
                            <p className="font-semibold text-[13px]">Date of Birth</p>
                            <p className="text-sportsDarkGrey text-[13px]">{formatDOB(participant.participantDOB)}</p>
                          </div>
                        )}
                        {participant.guestEmail && (
                          <div className="flex flex-col gap-y-1">
                            <p className="font-semibold text-[13px]">Email</p>
                            <p className="text-sportsDarkGrey text-[13px]">{participant.guestEmail}</p>
                          </div>
                        )}
                        {participant.primaryEmergencyContactName && (
                          <div className="flex flex-col gap-y-1">
                            <p className="font-semibold text-[13px]">Emergency Contact</p>
                            <p className="text-sportsDarkGrey text-[13px]">{participant.primaryEmergencyContactName}</p>
                          </div>
                        )}
                        {participant.primaryContactNumber && (
                          <div className="flex flex-col gap-y-1">
                            <p className="font-semibold text-[13px]">Contact Number</p>
                            <p className="text-sportsDarkGrey text-[13px]">{participant.primaryContactNumber}</p>
                          </div>
                        )}
                        {participant.primaryContactRelationship && (
                          <div className="flex flex-col gap-y-1">
                            <p className="font-semibold text-[13px]">Relationship</p>
                            <p className="text-sportsDarkGrey text-[13px]">{participant.primaryContactRelationship}</p>
                          </div>
                        )}
                        {participant.primaryMedicalCondition && (
                          <div className="flex flex-col gap-y-1">
                            <p className="font-semibold text-[13px]">Medical Condition</p>
                            <p className="text-sportsDarkGrey text-[13px]">{participant.primaryMedicalCondition}</p>
                          </div>
                        )}
                        {participant.notes && (
                          <div className="flex flex-col gap-y-1">
                            <p className="font-semibold text-[13px]">Notes</p>
                            <p className="text-sportsDarkGrey text-[13px]">{participant.notes}</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </DropDown>
              ))}
            </div>
          </div>
        )}

        {booking.bookingId && booking.status !== -1 && (
          <div>
            <p className="font-semibold text-[14px] mb-3">Booking QR Code</p>
            <div className="w-full rounded-[8px] border border-[#E3E3E3] bg-white p-[24px] flex flex-col items-center">
              <img
                src={`${import.meta.env.VITE_API_URL}QRCode/raw?data=${booking.bookingId}&format=png&ppm=5&dark=%23000000&light=%23FFFFFF&quiet=true&transparent=false`}
                alt="Booking QR Code"
                width={200}
                height={200}
                className="w-[200px] h-[200px]"
              />
            </div>
          </div>
        )}

        <div>
          <p className="font-semibold text-[14px] mb-1">Location</p>
          <p className="text-sportsDarkGrey mb-3">{fullAddress || "Address not yet available"}</p>
          {address && (
            <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}>
              <GeocodedMap address={fullAddress} />
            </APIProvider>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className='relative bg-sportsLightGrey min-h-screen p-[24px]'>
      <p className='uppercase text-[12px] font-semibold leading-[24px] text-sportsBlack tracking-widest'>{(booking.bookableThingName ?? booking.orgPartnerFriendlyName ?? "").replace(/\b\w/g, c => c.toUpperCase())}</p>
      <h2>{booking.dateSlot ? new Date(booking.dateSlot).toLocaleDateString("en-GB") : ""}{booking.startTime ? " • " + booking.startTime.slice(0, 5) : ""}</h2>
      <div className="flex flex-row gap-x-3 py-[25px]">
        <div
          className={`
            flex p-2 rounded-full font-medium cursor-pointer border-[2px] border-transparent bg-white justify-center items-center py-3 px-6 
            ${pillFilter === "All" ? "!border-sportsBlue" : ""}
          `}
          onClick={() => handlePillClick("All")}
        >
          <p className="text-[10px] flex justify-center">All</p>
        </div>

        <div
          className={`
            flex p-2 rounded-full font-medium cursor-pointer border-[2px] border-transparent bg-white justify-center items-center py-3 px-6 
            ${pillFilter === "Active" ? "!border-sportsBlue" : ""}
          `}
          onClick={() => handlePillClick("Active")}
        >
          <p className="text-[10px] flex justify-center">Booked In</p>
        </div>

        <div
          className={`
            flex p-2 rounded-full font-medium cursor-pointer border-[2px] border-transparent bg-white w-fit justify-center items-center py-3 px-6 
            ${pillFilter === "Not Assigned" ? "!border-sportsBlue" : ""}
          `}
          onClick={() => handlePillClick("Not Assigned")}
        >
          <p className="text-[10px] flex justify-center">Not Assigned</p>
        </div>
      </div>
      <div className="flex flex-col gap-y-[13px]">
      {filteredParticipants.map((participant, index) => {
          const isActive = participant.isBookedIn === 1 || participant.bandStatus === "active";
          const isIncomplete = participant.IsIncomplete === 1 || participant.isIncomplete === 1;

          const pillClass = isBookingCompleted
            ? "bg-slate-100 text-slate-500"
            : isActive
            ? "bg-sportsBlue text-white"
            : isIncomplete
            ? "bg-orange-100 text-orange-700 border border-orange-300"
            : "bg-white text-black border border-[#E3E3E3]";

          const pillLabel = isBookingCompleted
            ? "COMPLETED"
            : isActive
            ? "BOOKED IN"
            : isIncomplete
            ? "INCOMPLETE"
            : "NOT ASSIGNED";

          const cardInner = (
            <div
              className="flex flex-row justify-between rounded-[8px] border border-[#e3e3e3] bg-white p-[14px] min-h-[78px]"
            >
              <div className="flex flex-row">
                <img
                  src={participant.bandImageURL ?? `/images/default-avatar.svg`}
                  alt="Profile Image"
                  width={50}
                  height={50}
                  className="mr-4 rounded-[4px] object-contain"
                />
                <div className="flex flex-col justify-center">
                  <p className="font-semibold mb-2">{participant?.participantName}</p>
                  <p
                    className={`px-[9px] py-[3px] tracking-[2px] text-[11px] uppercase w-fit rounded-[4px] font-semibold ${pillClass}`}
                  >
                    {pillLabel}
                  </p>
                </div>
              </div>
              <div className="flex justify-center items-center align-middle">
                <ChevronIconRight className="w-6 h-6 fill-black" />
              </div>
            </div>
          );
          return isIncomplete ? (
            <div key={index} className="cursor-pointer" onClick={() => openIncompleteWarningDrawer(participant)}>
              {cardInner}
            </div>
          ) : (
            <Link to="/bookings/assign-band/scan" state={{ participant, bookingId: booking.bookingId ?? booking.BookingId, participantId: index + 1, allParticipants: booking.participants }} key={index}>
              {cardInner}
            </Link>
          );
        })}      </div>
    </div>
  );
}
